import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useGetProductCatalogQuery } from '@/api/hooks/queries/use-get-product-catalog-query';
import { NavigationBar } from '@/components/NavigationBar';
import { useEffectiveCategoryLabels } from '@/hooks/use-effective-category-labels';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { PRODUCT_STEP_ID_SUMMARY, type ProductStep } from '@/lib/products.ts';
import { type Step, type TanstackUrl, getCurrentStepIndex } from '@/lib/stepper.ts';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type.ts';
import { isInsuranceEligible } from '@/types/product.type.ts';

function createProfileStep(path: TanstackUrl): Step {
  return {
    id: 'create-profile',
    name: 'Create profile',
    url: path,
  };
}

type Props = {
  steps: ProductStep[];
};

export default function ProductStepper({ steps }: Props) {
  const navigate = useNavigate();
  const { insuranceBuilder, patientRecipient } = useSessionStore();
  const { data = [], isSuccess } = useGetProductCatalogQuery();
  const effectiveCategoryLabels = useEffectiveCategoryLabels();

  const selectedCategorySteps = useMemo(() => {
    return steps.filter(
      (step) => effectiveCategoryLabels.includes(step.id) || step.id === 'Summary'
    );
  }, [steps, effectiveCategoryLabels]);

  const categoryFilteredSteps = useMemo(
    () =>
      selectedCategorySteps.filter((step) => {
        const stepCategoryName = step.categoryType;
        if (!stepCategoryName) return true;

        // Until the catalog loads, keep selected steps so the bar still renders.
        if (!isSuccess) return true;

        const hasProductsInCategory = data.some(
          (product) =>
            product.category.name === stepCategoryName &&
            isInsuranceEligible(product, getActionablePlanFamilyCarrierTypes(insuranceBuilder))
        );
        return hasProductsInCategory;
      }),
    [isSuccess, data, insuranceBuilder, selectedCategorySteps]
  );

  const currStepIndex = getCurrentStepIndex(categoryFilteredSteps);
  const currStep = categoryFilteredSteps[currStepIndex];

  const categoryStepsOnly = useMemo(
    () => categoryFilteredSteps.filter((s) => s.id !== PRODUCT_STEP_ID_SUMMARY),
    [categoryFilteredSteps]
  );

  const preProductsStep = createProfileStep(
    patientRecipient === 'me' ? '/create-profile/for-me' : '/create-profile/for-someone-else'
  );
  const prevStep = currStepIndex > 0 ? categoryFilteredSteps[currStepIndex - 1]! : preProductsStep;

  if (!currStep) return null;

  const categoryPosition = categoryStepsOnly.findIndex((s) => s.id === currStep.id);
  const xOfY =
    categoryPosition >= 0
      ? `${categoryPosition + 1} of ${categoryStepsOnly.length}`
      : `${currStepIndex + 1} of ${categoryFilteredSteps.length}`;
  const description = currStep.description ? ` ${currStep.description}` : '';
  const indicator = `${xOfY}${description}`;

  return (
    <NavigationBar
      backLabel={prevStep.name}
      onBack={() => navigate({ to: prevStep.url })}
      stepIndicator={currStep.hideStepCount ? undefined : indicator}
    />
  );
}
